import { openDB, type DBSchema } from 'idb';
import { z } from 'zod';
import { mutationSchema, snapshotSchema, type Mutation, type Snapshot } from '../domain/model';

export const eventSchema = z.object({
  key: z.string(),
  scope: z.string(),
  mutation: mutationSchema,
  state: z.enum(['pending', 'conflict', 'invalid']),
  message: z.string().default(''),
  createdAt: z.number(),
});
export type StoredEvent = z.infer<typeof eventSchema>;
interface LocalSchema extends DBSchema {
  cache: { key: string; value: unknown };
  events: { key: string; value: StoredEvent; indexes: { 'by-scope': string } };
  leases: { key: string; value: { owner: string; expires: number } };
}
export function namespace(uid: string): string {
  return `lexispulse-test|${uid}|v2`;
}
export const localDB = () =>
  openDB<LocalSchema>('lexispulse-test-v2', 1, {
    upgrade(db) {
      db.createObjectStore('cache');
      db.createObjectStore('events', { keyPath: 'key' }).createIndex('by-scope', 'scope');
      db.createObjectStore('leases');
    },
  });

export async function saveLocal(scope: string, key: string, value: unknown): Promise<void> {
  const db = await localDB();
  const tx = db.transaction('cache', 'readwrite');
  await Promise.all([tx.store.put(value, `${scope}|${key}`), tx.done]);
}
export async function readLocal(scope: string, key: string): Promise<unknown> {
  return (await localDB()).get('cache', `${scope}|${key}`);
}
export async function clearNamespace(scope: string): Promise<void> {
  const db = await localDB();
  const tx = db.transaction(['cache', 'events', 'leases'], 'readwrite');
  const cache = tx.objectStore('cache');
  const events = tx.objectStore('events');
  const cacheKeys = await cache.getAllKeys();
  const eventKeys = await events.index('by-scope').getAllKeys(scope);
  for (const key of cacheKeys)
    if (typeof key === 'string' && key.startsWith(`${scope}|`)) await cache.delete(key);
  for (const key of eventKeys) await events.delete(key);
  await tx.objectStore('leases').delete(scope);
  await tx.done;
}
export async function loadSnapshot(scope: string): Promise<Snapshot | null> {
  const raw = await readLocal(scope, 'snapshot');
  if (raw === undefined) return null;
  const parsed = snapshotSchema.safeParse(raw);
  if (!parsed.success)
    throw new Error('離線資料版本不相容，請連線更新後重新開啟');
  return parsed.data;
}
export async function saveSnapshot(scope: string, snapshot: Snapshot): Promise<void> {
  if (namespace(snapshot.uid) !== scope) throw new Error('帳號快取不符');
  await saveLocal(scope, 'snapshot', snapshotSchema.parse(snapshot));
}
export async function enqueue(scope: string, mutation: Mutation): Promise<StoredEvent> {
  const value = eventSchema.parse({
    key: `${scope}|${mutation.mutationId}`,
    scope,
    mutation,
    state: 'pending',
    message: '',
    createdAt: Date.now(),
  });
  const db = await localDB();
  const tx = db.transaction('events', 'readwrite');
  try {
    const existing = await tx.store.get(value.key);
    if (existing && JSON.stringify(existing.mutation) !== JSON.stringify(value.mutation))
      throw new Error('寫入識別已被使用');
    if (!existing) await tx.store.add(value);
    await tx.done;
    return existing ?? value;
  } catch (error) {
    await tx.done.catch(() => undefined);
    throw error;
  }
}
export async function eventsFor(scope: string): Promise<StoredEvent[]> {
  const raw = await (await localDB()).getAllFromIndex('events', 'by-scope', scope);
  return raw
    .map((value) => eventSchema.parse(value))
    .sort((a, b) => a.createdAt - b.createdAt || a.mutation.occurredAt - b.mutation.occurredAt);
}
export async function acknowledge(event: StoredEvent): Promise<void> {
  const db = await localDB();
  const tx = db.transaction('events', 'readwrite');
  await Promise.all([tx.store.delete(event.key), tx.done]);
}
export async function retainProblem(
  event: StoredEvent,
  state: 'conflict' | 'invalid',
  message: string,
): Promise<void> {
  const db = await localDB();
  const tx = db.transaction('events', 'readwrite');
  await Promise.all([tx.store.put({ ...event, state, message }), tx.done]);
}

export async function withSyncLock<T>(
  scope: string,
  work: () => Promise<T>,
): Promise<T | undefined> {
  const locks = typeof navigator !== 'undefined' ? navigator.locks : undefined;
  if (locks)
    return locks.request(`lexispulse-sync:${scope}`, { ifAvailable: true }, (lock) =>
      lock ? work() : undefined,
    );
  const db = await localDB();
  const owner = crypto.randomUUID();
  const tx = db.transaction('leases', 'readwrite');
  const previous = await tx.store.get(scope);
  if (previous && previous.expires > Date.now()) {
    await tx.done;
    return undefined;
  }
  await tx.store.put({ owner, expires: Date.now() + 90000 }, scope);
  await tx.done;
  let lost = false;
  const interval = setInterval(() => {
    void (async () => {
      const renewal = db.transaction('leases', 'readwrite');
      const lease = await renewal.store.get(scope);
      if (lease?.owner !== owner) lost = true;
      else await renewal.store.put({ owner, expires: Date.now() + 90000 }, scope);
      await renewal.done;
    })().catch(() => {
      lost = true;
    });
  }, 15000);
  try {
    const result = await work();
    if (lost) throw new Error('同步鎖已失效，請重新同步');
    return result;
  } finally {
    clearInterval(interval);
    const cleanup = db.transaction('leases', 'readwrite');
    if ((await cleanup.store.get(scope))?.owner === owner) await cleanup.store.delete(scope);
    await cleanup.done;
  }
}
